import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  getDoc,
} from 'firebase/firestore';
import { applicationService } from '../services/applicationService';
import { showToast } from '../utils/toast';
import { formatDate } from '../utils/helpers';
import {
  getDifficultyLabel,
  getRecruitmentStatusLabel
} from '../utils/recruitmentDisplay';
import './ApplicationHistory.css';

function ApplicationHistory() {
  const { currentUser } = useAuth();
  const [appliedRecruitments, setAppliedRecruitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchApplicationHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);

      const appliedRecruitmentsData = applicationsSnapshot.docs.map(appSnapshot => {
        const application = { id: appSnapshot.id, ...appSnapshot.data() };

        return {
          id: application.recruitmentId,
          organizerName: application.organizerName || '不明',
          mountainName: application.mountainName || '',
          departureDate: application.departureDate || null,
          applicationId: application.id,
          applicationDate: application.createdAt,
          applicationStatus: application.applicationStatus || 'pending',
          needsFetch: true
        };
      });

      const needsFetch = appliedRecruitmentsData.filter(item => item.needsFetch);
      if (needsFetch.length > 0) {
        const recruitmentIds = [...new Set(needsFetch.map(item => item.id))];

        const recruitmentDocs = await Promise.all(
          recruitmentIds.map(async (recruitmentId) => {
            const recruitmentDoc = await getDoc(doc(db, 'recruitments', recruitmentId));
            return recruitmentDoc.exists() ? {
              id: recruitmentId,
              ...recruitmentDoc.data()
            } : null;
          })
        );

        const recruitmentMap = new Map(
          recruitmentDocs.filter(Boolean).map(d => [d.id, d])
        );

        needsFetch.forEach(item => {
          const recruitment = recruitmentMap.get(item.id);
          if (recruitment) {
            item.organizerName = recruitment.organizer?.nickname || '不明';
            item.mountainName = recruitment.mountainName || item.mountainName;
            item.departureDate = recruitment.departureDate || item.departureDate;
            item.route = recruitment.route || '';
            item.difficulty = recruitment.difficulty || '';
            item.applicationCount = recruitment.applicationCount || 0;
            item.maxParticipants = recruitment.maxParticipants || 0;
            item.status = recruitment.status || 'active';
          }
          delete item.needsFetch;
        });
      }

      setAppliedRecruitments(appliedRecruitmentsData.filter(item => !item.needsFetch));

    } catch (err) {
      console.error('応募履歴の取得に失敗しました:', err);
      setError('応募履歴の取得に失敗しました。時間をおいて再試行してください。');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchApplicationHistory();
    }
  }, [currentUser, fetchApplicationHistory]);

  const handleCancelApplication = async (applicationId) => {
    if (!window.confirm('応募をキャンセルしてもよろしいですか？')) {
      return;
    }

    try {
      await applicationService.cancel(applicationId);
      showToast({ message: '応募をキャンセルしました。', type: 'success' });
      fetchApplicationHistory();

    } catch (error) {
      console.error('応募キャンセルに失敗しました:', error);
      showToast({ message: '応募キャンセルに失敗しました。', type: 'error' });
    }
  };

  if (loading) {
    return <div className="loading">読み込み中...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="application-history-container">
      <h1>応募履歴</h1>

      <div className="recruitments-list">
        {appliedRecruitments.length > 0 ? (
          appliedRecruitments.map((recruitment) => (
            <div key={recruitment.applicationId} className="recruitment-card">
              <div className="card-header">
                <h3>{recruitment.mountainName}</h3>
                <span className={`difficulty ${recruitment.difficulty}`}>
                  {getDifficultyLabel(recruitment.difficulty)}
                </span>
              </div>

              <div className="card-body">
                <p className="route">ルート: {recruitment.route || '未設定'}</p>
                <p className="date">
                  📅 {formatDate(recruitment.departureDate)}
                </p>
                <p className="participants">
                  👥 {recruitment.applicationCount}/{recruitment.maxParticipants}人
                </p>
                <p className="organizer">
                  募集者: {recruitment.organizerName || recruitment.organizer?.nickname || '不明'}
                </p>
                <p className="apply-date">
                  応募日: {formatDate(recruitment.applicationDate)}
                </p>
                <p className={`status ${recruitment.status}`}>
                  {getRecruitmentStatusLabel(recruitment.status)}
                </p>
                {(() => {
                  const appStatus = recruitment.applicationStatus || 'pending';
                  const labels = { pending: '承認待ち', approved: '承認済み', rejected: '拒否済み' };
                  const classes = { pending: 'app-status-pending', approved: 'app-status-approved', rejected: 'app-status-rejected' };
                  return (
                    <span className={`app-status-badge ${classes[appStatus]}`}>
                      {labels[appStatus]}
                    </span>
                  );
                })()}
              </div>

              <div className="card-footer">
                <Link
                  to={`/recruitments/${recruitment.id}`}
                  className="btn btn-outline"
                >
                  詳細を見る
                </Link>
                {recruitment.status === 'active' && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleCancelApplication(recruitment.applicationId)}
                  >
                    応募をキャンセル
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="no-results">
            <p>応募した募集がありません。</p>
            <Link to="/recruitments" className="btn btn-primary">
              募集を探す
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default ApplicationHistory;
